import { useEffect, useMemo, useState, useRef } from "react";
import { Center, Flex, Loader, Text, Title, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { useDebouncedValue } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import {
  OrderStatusBadge,
  OrderTypeBadge,
  ORDER_STATUS_OPTIONS,
  ORDER_TYPE_OPTIONS,
} from "@/pages/admin/AdminStatusBadge";
import { formatINR, formatDate } from "@/shared/utils/format.utils";

type OrderListItem = {
  date: Date;
  orderId: string;
  username: string;
  schemeName: string;
  amount: number | null;
  orderType: "P" | "R" | "S";
  orderStatus: string;
  mode: string | null;
};

const PAGE_SIZE = 10;

export default function OrdersPage() {
  const theme = useMantineTheme();

  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const active = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(active.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });
    if (paramsString === previousParamsRef.current) return;
    previousParamsRef.current = paramsString;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const filter: Record<string, any> = {};
        if (filterParams.orderId) filter.orderId = filterParams.orderId;
        if (filterParams.username) filter.username = filterParams.username;
        if (filterParams.schemeName) filter.schemeName = filterParams.schemeName;
        if (filterParams.orderType) filter.orderType = filterParams.orderType;
        if (filterParams.orderStatus) filter.orderStatus = filterParams.orderStatus;

        const res = await trpc.orders.list.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setOrders(res.data.orders as OrderListItem[]);
        setTotalRows(res.data.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setOrders([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<OrderListItem>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 150,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <Text fz="sm">{formatDate(cell.getValue<Date>())}</Text>
        ),
      },
      {
        accessorKey: "orderId",
        header: "Order ID",
        size: 160,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "username",
        header: "Username",
        size: 160,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 140,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>
        ),
      },
      {
        accessorKey: "orderType",
        header: "Order type",
        size: 150,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ORDER_TYPE_OPTIONS,
          clearable: true,
        },
        enableSorting: true,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string>()} />,
      },
      {
        accessorKey: "orderStatus",
        header: "Order status",
        size: 190,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ORDER_STATUS_OPTIONS,
          clearable: true,
        },
        enableSorting: true,
        Cell: ({ cell }) => <OrderStatusBadge status={cell.getValue<string>()} />,
      },
      {
        accessorKey: "mode",
        header: "Mode",
        size: 130,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
    ],
    []
  );

  if (loading && orders.length === 0) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          All Orders
        </Title>
      </Flex>

      <MantineReactTable
        columns={columns}
        data={orders}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        paginationDisplayMode="pages"
        initialState={{
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No orders found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
